import { onMounted, ref } from 'vue'
import { api } from '@/api'
import { usersApi } from '@/api-users'
import type { SearchSelectOption } from '@/components/SearchSelect.vue'
import type { KpiSourceKind } from '@/lib/kpi'

/**
 * What a KPI's source is picked from and named by: the enterprise's people for a reading someone enters, its tasks
 * for one a task records. Read once by whoever uses it. A read that fails leaves its list empty, so a source already
 * set is still shown, as its id.
 */
export function useKpiSources() {
    const people = ref<SearchSelectOption[]>([])
    const tasks = ref<SearchSelectOption[]>([])

    onMounted(async () => {
        const [taskRows, userRows] = await Promise.all([
            api.tasks({ limit: 200 }).catch(() => null),
            usersApi.users().catch(() => []),
        ])
        tasks.value = (taskRows?.data ?? []).map(task => ({
            id: task.id,
            label: task.name || task.slug,
            search: task.slug,
        }))
        people.value = userRows.map(person => ({ id: person.id, label: person.name }))
    })

    function options(kind: KpiSourceKind | null): SearchSelectOption[] {
        if (kind === 'person') return people.value
        return kind === 'task' ? tasks.value : []
    }

    /** The source's name, or null when the lists do not hold it: the caller then shows the id it has. */
    function name(kind: KpiSourceKind | null, sourceRef: string | null): string | null {
        return options(kind).find(option => option.id === sourceRef)?.label ?? null
    }

    return { options, name }
}
